import Utils from '../components/utils'
import nameFormatter from './nameFormatter'
import TOKENS from './tokens'

const HEATMAP_MODE = {
    OFF: 'OFF',
    FREQUENCY: 'FREQUENCY',
    DURATION: 'DURATION',
}

const HEATMAP_COLORS = {
    low: '#fff36f',
    high: '#f63737',
}

const clamp01 = (value) => Math.max(0, Math.min(1, value))

const parseNumeric = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value !== 'string') return null
    const normalized = value.replace(/,/g, '').trim()
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : null
}

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return null
    const clean = hex.replace('#', '').trim()
    if (clean.length !== 6) return null
    const intVal = parseInt(clean, 16)
    return {
        r: (intVal >> 16) & 255,
        g: (intVal >> 8) & 255,
        b: intVal & 255,
    }
}

const rgbToHex = ({ r, g, b }) => {
    const asHex = (n) => n.toString(16).padStart(2, '0')
    return `#${asHex(r)}${asHex(g)}${asHex(b)}`
}

const interpolateHex = (fromHex, toHex, ratio) => {
    const from = hexToRgb(fromHex)
    const to = hexToRgb(toHex)
    if (!from || !to) return TOKENS.node.defaultBg

    const t = clamp01(ratio)
    return rgbToHex({
        r: Math.round(from.r + (to.r - from.r) * t),
        g: Math.round(from.g + (to.g - from.g) * t),
        b: Math.round(from.b + (to.b - from.b) * t),
    })
}

const getTextColorForBackground = (hex) => {
    const rgb = hexToRgb(hex)
    if (!rgb) return '#000000'
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255
    return luminance < 0.55 ? '#ffffff' : '#000000'
}

const getHeatMetricForNode = (node, model) => {
    const mode = model?.heatmapMode || HEATMAP_MODE.OFF
    if (mode === HEATMAP_MODE.FREQUENCY) {
        return parseNumeric(node.data('freq'))
    }
    if (mode === HEATMAP_MODE.DURATION) {
        return parseNumeric(Utils.getStatValue(node.data('statistics')?.duration, model?.statsValue))
    }
    return null
}

const getHeatMetricForEdge = (edge, model) => {
    const mode = model?.heatmapMode || HEATMAP_MODE.OFF
    if (mode === HEATMAP_MODE.FREQUENCY) {
        return parseNumeric(edge.data('freq'))
    }
    if (mode === HEATMAP_MODE.DURATION) {
        return parseNumeric(Utils.getStatValue(edge.data('statistics')?.duration, model?.statsValue))
    }
    return null
}

const buildNodeHeatColorMap = (cy, model) => {
    const mode = model?.heatmapMode || HEATMAP_MODE.OFF
    if (mode === HEATMAP_MODE.OFF) return new Map()

    const metrics = []
    const metricByNode = new Map()

    cy.nodes().forEach((node) => {
        const nodeId = node.data('id')
        if (nodeId === 'START' || nodeId === 'END') return

        const metric = getHeatMetricForNode(node, model)
        if (metric === null) return

        metricByNode.set(nodeId, metric)
        metrics.push(metric)
    })

    if (!metrics.length) return new Map()

    const min = Math.min(...metrics)
    const max = Math.max(...metrics)
    const range = max - min

    const result = new Map()
    metricByNode.forEach((metric, nodeId) => {
        const ratio = range > 0 ? (metric - min) / range : 0.5
        result.set(nodeId, interpolateHex(HEATMAP_COLORS.low, HEATMAP_COLORS.high, ratio))
    })

    return result
}

const buildEdgeHeatColorMap = (cy, model) => {
    const mode = model?.heatmapMode || HEATMAP_MODE.OFF
    if (mode === HEATMAP_MODE.OFF) return new Map()

    const metrics = []
    const metricByEdge = new Map()

    cy.edges().forEach((edge) => {
        if (edge.hasClass('start') || edge.hasClass('end')) return

        const metric = getHeatMetricForEdge(edge, model)
        if (metric === null) return

        const edgeId = edge.data('id')
        metricByEdge.set(edgeId, metric)
        metrics.push(metric)
    })

    if (!metrics.length) return new Map()

    const min = Math.min(...metrics)
    const max = Math.max(...metrics)
    const range = max - min

    const result = new Map()
    metricByEdge.forEach((metric, edgeId) => {
        const ratio = range > 0 ? (metric - min) / range : 0.5
        result.set(edgeId, interpolateHex(HEATMAP_COLORS.low, HEATMAP_COLORS.high, ratio))
    })

    return result
}

const applyGraphSemanticStyles = ({
    cy,
    graphData,
    model,
}) => {
    if (!cy || !graphData) return

    const {
        loops_activities: loopsActivities,
        loop_arcs: loopArcs,
        arcs: allArcs,
    } = graphData

    const loopsActivitiesIds = (loopsActivities || []).map((act) => act.name)
    const heatColorByNode = buildNodeHeatColorMap(cy, model)
    const heatColorByEdge = buildEdgeHeatColorMap(cy, model)

    const loopsArcsId = (loopArcs || []).map((arc) => `arc-node-${nameFormatter(arc.name)}`)

    cy.batch(() => {
        cy.nodes().forEach((node) => {
            const nodeId = node.data('id')

            node.removeClass('is-grouped is-loop')

            // Base border color (heatmap-derived)
            let borderColor = ''
            if (nodeId === 'START') {
                borderColor = TOKENS.node.startBorder
            } else if (nodeId === 'END') {
                borderColor = TOKENS.node.endBorder
            } else {
                borderColor = TOKENS.node.normalBorder
            }

            const nodeHeatColor = heatColorByNode.get(nodeId)
            const bgColor = nodeHeatColor || TOKENS.node.defaultBg
            const textColor = getTextColorForBackground(bgColor)

            // Loop styling overrides heat
            if (loopsActivitiesIds.includes(nodeId)) {
                node.addClass('is-loop')
            }

            node.data('borderColor', borderColor)
            node.data('bgColor', bgColor)
            node.data('textColor', textColor)
        })

        cy.edges().forEach((edge) => {
            const edgeId = edge.data('id')

            edge.removeClass('is-loop is-overlap')

            const edgeHeatColor = heatColorByEdge.get(edgeId)
            const lineColor = edgeHeatColor || TOKENS.edge.normalLine
            edge.data('lineColor', lineColor)

            if (loopsArcsId.includes(edgeId)) {
                edge.addClass('is-loop')
            }
        })
    })
}

export default applyGraphSemanticStyles
